import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { logCustomEvent } from "../datadog";
import type { FilterType } from "./filter-keys";
import { filtersConfig } from "./filters-config";
import { type SortEntry, sortToUrl, urlToSort } from "./sort";

export type { FilterType };

export interface FilterEntry<T = any> {
  type: FilterType;
  value: T;
}

function urlToFilter(params: URLSearchParams): Array<FilterEntry> {
  return Object.values(filtersConfig)
    .map((config) => config.fromUrl(params))
    .filter((v) => v)
    .map((v) => v as FilterEntry);
}

function filterToUrl(
  filters: Array<FilterEntry>,
): Record<string, string | string[]> {
  return filters
    .map((filter) => filtersConfig[filter.type].toUrl(filter.value))
    .reduce((r, l) => Object.assign(r, l), {});
}

export function useFilters(): [
  filters: Array<FilterEntry>,
  setFilters: (filters: Array<FilterEntry>) => void,
  sort: Array<SortEntry>,
  setSort: (filters: Array<SortEntry>) => void,
] {
  const [urlParams, setUrlParams] = useSearchParams();
  const filters = useMemo(() => urlToFilter(urlParams), [urlParams]);
  const sort = useMemo(() => urlToSort(urlParams), [urlParams]);
  const setFilters = (filters: Array<FilterEntry>) =>
    setUrlParams(
      { ...filterToUrl(filters), ...sortToUrl(sort) },
      { replace: true },
    );
  const setSort = (sort: Array<SortEntry>) =>
    setUrlParams(
      { ...filterToUrl(filters), ...sortToUrl(sort) },
      { replace: true },
    );
  return [filters, setFilters, sort, setSort];
}

function hasValue<T>(v: T) {
  return Array.isArray(v) ? v.length > 0 : v !== undefined;
}

export function useSingleFilter<T>(
  filters: Array<FilterEntry>,
  setFilters: (filter: Array<FilterEntry>) => void,
  type: FilterType,
): [T | undefined, (v: T) => void] {
  const value: T | undefined = filters.filter((f) => f.type === type)[0]?.value;
  const setValue = useCallback(
    (v: T) => {
      logCustomEvent("filter-change", {
        filter_type: type,
        filter_value: JSON.stringify(v),
      });
      const newValue = {
        type: type,
        value: v,
      };

      let newFilter: Array<FilterEntry> = [];
      if (type === "industry") {
        newFilter = filters.filter(
          (f) =>
            f.type !== "applicationClass" &&
            f.type !== "application" &&
            f.type !== "grade",
        );
      } else if (type === "applicationClass") {
        newFilter = filters.filter(
          (f) => f.type !== "application" && f.type !== "grade",
        );
      } else if (type === "application") {
        newFilter = filters.filter((f) => f.type !== "grade");
      } else {
        newFilter = filters;
      }

      if (hasValue(v)) {
        if (newFilter.some((f) => f.type === type)) {
          setFilters(newFilter.map((f) => (f.type === type ? newValue : f)));
        } else {
          setFilters([...newFilter, newValue]);
        }
      } else {
        setFilters(newFilter.filter((f) => f.type !== type));
      }
    },
    [type, filters, setFilters],
  );
  return [value, setValue];
}

export const useTitleForFilters = (filters: Array<FilterEntry>) => {
  return useMemo(() => {
    return filters
      .map((filter) => filtersConfig[filter.type].title(filter.value))
      .filter((it) => it);
  }, [filters]);
};
